import { useId, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { userIdAtom } from "@/atoms/authAtoms";
import { Button } from "@/design-system/components/react";
import { Hint } from "@/components/devicePanel/Hint";
import { FindingFeedback, MissedFindings } from "@/components/devicePanel/NotificationFeedback";
import type { StudyReviewModel } from "@/components/devicePanel/Review";
import { ReviewSwitch, useRememberedPanelMode } from "@/components/devicePanel/ReviewSwitch";
import { ResearchBanner } from "@/components/devicePanel/ResearchTag";
import { KeyImagePreview } from "@/components/triage/TriageNotificationPanel";
import { useAuth } from "@/context/AuthContext";
import { panelImagesUrl, useSaveVerdict, useStudyReview } from "@/hooks/useDevicePanel";
import { useFindingSlices } from "@/hooks/useFindingSlices";
import { useStudyMetadata, type GalleryFinding } from "@/hooks/useStudyQueries";
import { clinicianNotes, deviceNotice, isFlagged } from "@/types/notificationView";
import { StepButtons, StepCounter, useCaseSteps } from "@/components/devicePanel/CaseSteps";
import {
  panelCaseUrl,
  panelUrl,
  type DevicePanelFinding,
  type DevicePanelStudyDevice,
  type DevicePanelStudyResponse,
} from "@/types/devicePanel";
import type { StudyMetadataResponse } from "@/utils/dicomMetadataLoader";

/** In this demo no PACS is connected; at a live site the button opens the study there. */
const PACS_NOTE = "Opens in the site's PACS at a live site";

const STEP = "min-h-11 sm:min-h-0";

/** The device a finding came from, kept small: its name and version. */
function DeviceLine({ device }: { device: DevicePanelStudyDevice }) {
  return (
    <span className="font-mono text-[10px] text-fg-dim">
      {[device.label, device.version && `v${device.version}`].filter(Boolean).join(" · ")}
    </span>
  );
}

/** One card per finding a device flagged: the device's key image (clean
 * pixels), the finding's name, a small device line and, for a cleared device,
 * its caption, with a reviewer's feedback under it. Rendered into the page's
 * shared grid. */
function FindingCards({
  device,
  findings,
  frame,
  feedback,
}: {
  device: DevicePanelStudyDevice;
  findings: DevicePanelFinding[];
  frame: (finding: DevicePanelFinding) => { keyFrame?: string; loading: boolean } | undefined;
  /** The caller's review, when they are one of the panel's reviewers. */
  feedback: StudyReviewModel | null;
}) {
  const caption = device.labeling?.key_image_caption ?? null;
  return findings.map((finding) => {
    const state = frame(finding);
    return (
      <li
        key={`${device.key}:${finding.condition}`}
        aria-label={finding.display_name}
        className="flex min-w-0 flex-col gap-1"
      >
        <KeyImagePreview
          name={finding.display_name}
          frame={state?.keyFrame}
          loading={finding.top_slices.length > 0 && state?.loading !== false}
          caption={caption}
          named
        />
        <DeviceLine device={device} />
        {feedback && (
          <FindingFeedback
            review={feedback}
            target={{ device: device.key, condition: finding.condition }}
          />
        )}
      </li>
    );
  });
}

/** A device's finding cards once the study's images are read through the panel. */
function LoadedFindingCards({
  studyUid,
  device,
  findings,
  images,
  feedback,
}: {
  studyUid: string;
  device: DevicePanelStudyDevice;
  findings: DevicePanelFinding[];
  images: StudyMetadataResponse;
  feedback: StudyReviewModel | null;
}) {
  const gallery = useMemo<GalleryFinding[]>(
    () =>
      findings.map((finding) => ({
        condition: finding.condition,
        category: null,
        topSlice: finding.top_slices[0] ?? null,
        advancement: null,
        percentile: null,
      })),
    [findings],
  );
  const slices = useFindingSlices(studyUid, device.key, gallery, images);
  return (
    <FindingCards
      device={device}
      findings={findings}
      frame={(f) => slices[f.condition]}
      feedback={feedback}
    />
  );
}

/** One device's line in the devices summary: its status (e.g. "FDA cleared"),
 * version and what it reported, or why it has nothing to report. */
function DeviceSummary({ device }: { device: DevicePanelStudyDevice }) {
  const notice = deviceNotice(device);
  const outcome =
    notice.kind === "flagged"
      ? `suspected ${notice.findings.length}`
      : notice.kind === "no_flag"
        ? "No flag"
        : notice.kind === "not_sent"
          ? ["Not sent", notice.reason].filter(Boolean).join(" · ")
          : [notice.text, notice.message].filter(Boolean).join(" · ");
  return (
    <li aria-label={device.label} className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-fg">{device.label}</span>
      <span className="font-mono text-[11px] text-fg-dim">
        {[device.status_label, device.version && `v${device.version}`, outcome]
          .filter(Boolean)
          .join(" · ")}
      </span>
    </li>
  );
}

/**
 * One study of a notification-view panel as its notification: the exam, its
 * suspected findings as cards (each with its key image, a cleared device's
 * caption and a small line naming the device), a compact line per device (what
 * it reported, or why it has nothing), and the cleared devices' clinician
 * notes. A panel reviewer can switch on Feedback (off until they do, so a live
 * demo shows the plain page) to judge each finding and its key image, and what
 * the devices missed. No viewer: the study opens in the site's PACS (or, in a
 * demo, the full viewer). Prev / Next (and ← / →)
 * step through the flagged studies in worklist order. At phone width the page
 * is one column with its actions in a bottom bar.
 */
export function NotificationPage({ study }: { study: DevicePanelStudyResponse }) {
  const { token } = useAuth();
  const userId = useAtomValue(userIdAtom);
  const pacsNote = useId();
  const review = useStudyReview(token, study.panel_id, study.study_uid, true);
  const saveVerdict = useSaveVerdict(token, study.panel_id, study.study_uid);
  const [mode, setMode] = useRememberedPanelMode(study.panel_id);
  const canReview = !!review.data?.can_review;
  const feedback: StudyReviewModel | null =
    canReview && mode === "review" ? { data: review.data!, save: saveVerdict.mutateAsync } : null;
  const flagsImage = study.devices.some((device) => {
    const notice = deviceNotice(device);
    return notice.kind === "flagged" && notice.findings.some((f) => f.top_slices.length > 0);
  });
  // The panel's own images route: a panel user may hold no other access to the study.
  const images = useStudyMetadata(token, userId, study.study_uid, {
    imagesUrl: panelImagesUrl(study.panel_id, study.study_uid),
    enabled: flagsImage,
  });
  const steps = useCaseSteps(study.panel_id, study.study_uid, { counted: isFlagged });

  const age =
    study.age_recorded === false
      ? "age not recorded"
      : study.age_years === null
        ? null
        : `${study.age_years} y`;
  const notes = clinicianNotes(study.devices);
  const flagged = study.devices.flatMap((device) => {
    const notice = deviceNotice(device);
    return notice.kind === "flagged" ? [{ device, findings: notice.findings }] : [];
  });
  const suspected = flagged.reduce((total, { findings }) => total + findings.length, 0);
  return (
    <div className="flex h-full flex-col bg-bg text-fg">
      <ResearchBanner label={study.research_label} />
      <header className="flex min-h-12 items-center gap-3 border-b border-border px-4 py-2 text-xs">
        <Link
          to={panelUrl(study.panel_id)}
          className="shrink-0 text-[13px] text-fg-muted hover:text-fg"
        >
          ← Back to list
        </Link>
        <div className="ml-auto flex items-center gap-3">
          {canReview && (
            <ReviewSwitch
              label="Feedback"
              on={!!feedback}
              onChange={(on) => setMode(on ? "review" : "view")}
            />
          )}
          {steps && <StepCounter steps={steps} noun="flagged case" />}
          {/* Beside the counter on a wide screen; a bottom bar at phone width. */}
          <div className="fixed inset-x-0 bottom-0 z-10 grid grid-cols-[1fr_1fr_2fr] gap-2 border-t border-border bg-bg-2 p-3 sm:static sm:z-auto sm:flex sm:items-center sm:border-0 sm:bg-transparent sm:p-0">
            <StepButtons
              panelId={study.panel_id}
              steps={steps}
              noun="flagged case"
              className={STEP}
            />
            {study.open_study === "viewer" ? (
              <Button asChild size="sm" className={STEP}>
                <Link to={`${panelCaseUrl(study.panel_id, study.study_uid)}?read=1`}>
                  Open study
                </Link>
              </Button>
            ) : (
              <>
                <Hint label={PACS_NOTE}>
                  <Button
                    type="button"
                    size="sm"
                    aria-disabled="true"
                    aria-describedby={pacsNote}
                    className={`${STEP} cursor-not-allowed opacity-50 hover:bg-signal`}
                  >
                    Open study in PACS
                  </Button>
                </Hint>
                <span id={pacsNote} className="sr-only">
                  {PACS_NOTE}
                </span>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-auto px-4 pb-28 pt-6 sm:px-6 sm:pb-8">
        <div className="mx-auto flex max-w-[1040px] flex-col gap-6">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim">
              Triage notification
            </span>
            <h1 className="text-xl font-semibold sm:text-2xl">{study.exam ?? "—"}</h1>
            <p className="text-sm text-fg-muted">
              {age && `${age} · `}
              <span className="font-mono">{study.label}</span>
            </p>
            <p className="mt-1 text-sm font-medium">
              {suspected === 0
                ? "No suspected findings"
                : `${suspected} suspected finding${suspected === 1 ? "" : "s"}`}
            </p>
          </div>
          {suspected > 0 && (
            <ul
              aria-label="Suspected findings"
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {flagged.map(({ device, findings }) =>
                images.data ? (
                  <LoadedFindingCards
                    key={device.key}
                    studyUid={study.study_uid}
                    device={device}
                    findings={findings}
                    images={images.data}
                    feedback={feedback}
                  />
                ) : (
                  <FindingCards
                    key={device.key}
                    device={device}
                    findings={findings}
                    frame={() => ({ loading: !images.isError })}
                    feedback={feedback}
                  />
                ),
              )}
            </ul>
          )}
          {feedback && <MissedFindings review={feedback} devices={study.devices} />}
          <ul aria-label="Devices" className="grid gap-1 border-t border-border pt-4 text-xs">
            {study.devices.map((device) => (
              <DeviceSummary key={device.key} device={device} />
            ))}
          </ul>
          <footer className="grid gap-1 border-t border-border pt-4 text-xs text-fg-muted">
            {notes.map((note) => (
              <p key={note}>{note}</p>
            ))}
          </footer>
        </div>
      </main>
    </div>
  );
}
